import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { toast } from 'react-toastify';
import RailIcon from './SidebarIcons';
import {
  getAddPatientModalNavigationTarget,
  isPracticeHubRoute,
  resolvePracticeIdFromUserOrPath,
} from '../../../utils/practiceNavigation';
import { getRoleId } from '../../../utils/roleUtils';
import sidebarPrefsService from '../../../services/sidebarPrefsService';
import {
  applySidebarOrder,
  getSidebarItemId,
  SIDEBAR_ORDER_CHANGED_EVENT
} from '../../../utils/sidebarOrder';

const SHORT_LABELS = {
  'Practice Enrollment': 'Enrollment',
  'System Admins': 'Admins',
  'System Caregivers': 'Caregivers',
  'Patient Management': 'Patient Mgmt',
  'Care Management': 'Care Mgmt',
  'Calendar Setting': 'Calendar',
  'Care Programs': 'Programs',
  'General Settings': 'Settings',
  'Mobile App Access': 'Mobile App',
  'Email Logs': 'Email Logs',
  'Look up Patient': 'Lookup Patient',
  'Look up Patient (By RPM Date)': 'Lookup Patient (by RPM date)',
  'Billing Reports': 'Billing Reports',
};

const Sidebar = ({ user, isOpen, onClose, isMobileView = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [openFlyoutKey, setOpenFlyoutKey] = useState(null);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const closeTimerRef = useRef(null);
  const railItemRefs = useRef({});
  const [savedOrder, setSavedOrder] = useState([]);

  const roleId = getRoleId(user);

  const isActive = (path) => {
    const fromDashboard = !!location.state?.fromDashboard;
    const pathname = location.pathname;

    if (path === '/settings') {
      return pathname === '/settings' || pathname.startsWith('/permission');
    }

    // Patient home — keep Dashboard highlighted on their info screen
    if (path === '/patient-dashboard') {
      return (
        pathname === '/patient-dashboard' ||
        pathname.startsWith('/patient-dashboard/')
      );
    }

    if (path === '/billing/sent-reports') {
      return pathname === '/billing/sent-reports' || pathname.startsWith('/billing/sent-reports/');
    }

    if (path === '/billing') {
      return (
        pathname === '/billing' ||
        (pathname.startsWith('/billing/') && !pathname.startsWith('/billing/sent-reports'))
      );
    }

    if (path === '/dashboard') {
      if (Number(roleId) === 3) return pathname === '/dashboard';
      return isPracticeHubRoute(pathname);
    }

    if (/^\/practices\/\d+$/.test(path)) {
      return pathname === path && isPracticeHubRoute(pathname);
    }

    if (fromDashboard) {
      if (path === '/dashboard') return true;
      if (path === '/practices/add' || path === '/practices') return false;
    }

    return pathname === path || pathname.startsWith(path + '/');
  };

  function getPatientLookupChildren() {
    return [
      { label: 'Look up Patient', path: '/patients/lookup' },
    ];
  }

  function getPatientManagementChildren() {
    return [
      { label: 'Add New Patient', action: 'open-add-patient' },
      ...getPatientLookupChildren(),
    ];
  }

  function getPracticeStaffMenuItems(dashboardPath = '/dashboard', dashboardLabel = 'Dashboard') {
    return [
      {
        section: 'DASHBOARD',
        items: [
          { path: dashboardPath, label: dashboardLabel, icon: 'fa-th-large' }
        ]
      },
      {
        section: 'MANAGEMENT',
        items: [
          {
            key: 'patient-mgmt', label: 'Patient Management', icon: 'fa-user-md', type: 'dropdown',
            children: getPatientManagementChildren()
          },
          {
            key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
            children: [
              { label: 'Follow up', path: '/care/follow-up' }
            ]
          },
          {
            key: 'calendar-setting', label: 'Calendar Setting', icon: 'fa-calendar', type: 'dropdown',
            children: [
              { label: 'Visit Type', path: '/calendar-setting/visit-type' },
              { label: 'Working Hours', path: '/calendar-setting/working-hours' },
              { label: 'Calendar', path: '/calendar-setting/calendar' }
            ]
          },
          { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
        ]
      },
      {
        section: 'SETTINGS',
        items: [
          { path: '/settings', label: 'Settings', icon: 'fa-cog' }
        ]
      },
    ];
  }

  function getMenuItems(roleId) {
    const normalizedRoleId = Number(roleId);
    const adminManagementItems = [
      {
        section: 'DASHBOARD',
        items: [
          { path: '/dashboard', label: 'Dashboard', icon: 'fa-th-large' },
        ]
      },
      {
        section: 'ADMINISTRATION',
        items: [
          { path: '/practices/add', label: 'Practice Enrollment', icon: 'fa-building' },
          { path: '/admin', label: 'System Admins', icon: 'fa-user-circle' },
          { path: '/system-caregiver-dashboard', label: 'System Caregivers', icon: 'fa-users' },
          { path: '/inventory', label: 'Inventory', icon: 'fa-cube' },
          {
            key: 'billing', label: 'Billing', icon: 'fa-credit-card', type: 'dropdown',
            children: [
              { label: 'Billing', path: '/billing', icon: 'fa-credit-card' },
              { label: 'Billing Reports', path: '/billing/sent-reports', icon: 'fa-file-text' }
            ]
          },
        ]
      },
      {
        section: 'MANAGEMENT',
        items: [
          {
            key: 'patient-mgmt', label: 'Patient Management', icon: 'fa-user-md', type: 'dropdown',
            children: getPatientLookupChildren()
          },
          {
            key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
            children: [
              { label: 'Follow up', path: '/care/follow-up' }
            ]
          },
          {
            key: 'calendar-setting', label: 'Calendar Setting', icon: 'fa-calendar', type: 'dropdown',
            children: [
              { label: 'Visit Type', path: '/calendar-setting/visit-type' },
              { label: 'Working Hours', path: '/calendar-setting/working-hours' },
              { label: 'Calendar', path: '/calendar-setting/calendar' }
            ]
          },
          { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
        ]
      },
      {
        section: 'SETTINGS',
        items: [
          { path: '/care-programs', label: 'Care Programs', icon: 'fa-cube' },
          { path: '/settings', label: 'General Settings', icon: 'fa-cog' },
        ]
      },
    ];

    switch (normalizedRoleId) {
      case 1:
        return adminManagementItems.map((group) => {
          if (group.section === 'MANAGEMENT') {
            return group;
          }
          if (group.section !== 'SETTINGS') return group;
          // Keep Settings as the final rail item
          return {
            ...group,
            items: [
              { path: '/care-programs', label: 'Care Programs', icon: 'fa-cube' },
              { path: '/settings', label: 'General Settings', icon: 'fa-cog' },
            ]
          };
        });

      case 2:
        return adminManagementItems.map((group) => {
          if (group.section === 'ADMINISTRATION') {
            return {
              ...group,
              items: group.items
                .filter((item) => item.path !== '/practices/add' && item.path !== '/admin' && item.path !== '/inventory')
                .map((item) => (
                  item.key === 'billing'
                    ? { path: '/billing', label: 'Billing', icon: 'fa-credit-card' }
                    : item
                ))
            };
          }
          if (group.section === 'SETTINGS') {
            return {
              ...group,
              items: group.items.filter((item) => item.path !== '/care-programs')
            };
          }
          return group;
        });

      case 3: {
        const practiceHome = user?.practice_id ? `/practices/${user.practice_id}` : '/dashboard';
        return getPracticeStaffMenuItems(practiceHome, 'Overview').map((group) => {
          if (group.section === 'DASHBOARD') {
            return {
              ...group,
              items: [
                { key: 'analytics', path: '/dashboard', label: 'Dashboard', icon: 'fa-th-large' },
                ...group.items.map((item) => ({ ...item, icon: 'fa-building' }))
              ]
            };
          }
          if (group.section === 'MANAGEMENT') {
            return {
              ...group,
              items: [
                ...group.items,
                {
                  path: '/billing/sent-reports',
                  label: 'Billing Reports',
                  icon: 'fa-file-text'
                }
              ]
            };
          }
          return group;
        });
      }

      case 4:
        return getPracticeStaffMenuItems();

      case 5:
        return [
          {
            section: 'DASHBOARD',
            items: [
              { path: '/dashboard', label: 'Dashboard', icon: 'fa-th-large' }
            ]
          },
          {
            section: 'MANAGEMENT',
            items: [
              {
                key: 'patient-mgmt', label: 'Patient Management', icon: 'fa-user-md', type: 'dropdown',
                children: getPatientManagementChildren()
              },
              {
                key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
                children: [
                  { label: 'Follow up', path: '/care/follow-up' }
                ]
              },
              { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
            ]
          },
          {
            section: 'SETTINGS',
            items: [
              { path: '/settings', label: 'Settings', icon: 'fa-cog' }
            ]
          },
        ];

      case 6:
        return [
          {
            section: 'DASHBOARD',
            items: [
              { path: '/patient-dashboard', label: 'Dashboard', icon: 'fa-th-large' },
            ]
          },
          {
            section: 'MESSAGES',
            items: [
              { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
            ]
          },
          {
            section: 'SETTINGS',
            items: [
              { path: '/patient-settings', label: 'Settings', icon: 'fa-cog' }
            ]
          }
        ];

      case 7: {
        const practiceMatch = location.pathname.match(/^\/practices\/(\d+)/);
        const currentPracticeId = practiceMatch ? practiceMatch[1] : null;

        const systemCaregiverManagementItems = [
          {
            key: 'patient-mgmt', label: 'Patient Management', icon: 'fa-user-md', type: 'dropdown',
            children: getPatientLookupChildren()
          },
          {
            key: 'care-mgmt', label: 'Care Management', icon: 'fa-heartbeat', type: 'dropdown',
            children: [
              { label: 'Follow up', path: '/care/follow-up' }
            ]
          },
          { path: '/chat', label: 'Messages', icon: 'fa-envelope' },
        ];

        if (currentPracticeId) {
          return [
            {
              section: 'DASHBOARD',
              items: [
                { path: '/system-caregiver-dashboard', label: 'Practices', icon: 'fa-building' },
              ]
            },
            {
              section: 'MANAGEMENT',
              items: systemCaregiverManagementItems
            },
            {
              section: 'SETTINGS',
              items: [
                { path: '/settings', label: 'Settings', icon: 'fa-cog' }
              ]
            },
          ];
        }

        return [
          {
            section: 'DASHBOARD',
            items: [
              { path: '/system-caregiver-dashboard', label: 'Practice', icon: 'fa-building' },
            ]
          },
          {
            section: 'MANAGEMENT',
            items: systemCaregiverManagementItems
          },
          {
            section: 'SETTINGS',
            items: [
              { path: '/settings', label: 'Settings', icon: 'fa-cog' }
            ]
          },
        ];
      }

      default:
        return [];
    }
  }

  const menuGroups = getMenuItems(user?.role_id);
  const catalogItems = menuGroups
    .flatMap((group) => group.items)
    .map((item) => ({ ...item, id: getSidebarItemId(item, roleId) }));
  const railItems = applySidebarOrder(catalogItems, savedOrder, roleId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await sidebarPrefsService.get();
        if (cancelled) return;
        const order = Array.isArray(response?.data?.order) ? response.data.order : [];
        setSavedOrder(order);
      } catch (err) {
        console.error('Failed to load sidebar order', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, roleId]);

  useEffect(() => {
    const onOrderChanged = (event) => {
      const order = event?.detail?.order;
      if (Array.isArray(order)) setSavedOrder(order);
    };
    window.addEventListener(SIDEBAR_ORDER_CHANGED_EVENT, onOrderChanged);
    return () => window.removeEventListener(SIDEBAR_ORDER_CHANGED_EVENT, onOrderChanged);
  }, []);

  const getShortLabel = (label) => SHORT_LABELS[label] || label;

  const isDropdownActive = (item) =>
    item.children?.some((child) => child.path && isActive(child.path));

  const handleOpenAddPatient = () => {
    const practiceId = resolvePracticeIdFromUserOrPath(user, location.pathname);
    if (!practiceId && Number(user?.role_id) !== 4 && Number(user?.role_id) !== 5) {
      toast.warn('No practice is assigned to your account. Please contact your administrator.');
      return;
    }
    handleNavigate(getAddPatientModalNavigationTarget(user, location.pathname));
  };

  const handleChildNavigate = (child) => {
    if (child.disabled) {
      toast.info(
        child.path?.includes('lookup-ccm')
          ? 'This practice is not enrolled in CCM.'
          : child.path?.includes('lookup-rpm')
            ? 'This practice is not enrolled in RPM.'
            : 'This option is not available for your practice.'
      );
      return;
    }
    if (child.action === 'open-add-patient') {
      handleOpenAddPatient();
      return;
    }
    if (child.path) {
      handleNavigate(child.path);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setOpenFlyoutKey(null);
    if (isMobileView && typeof onClose === 'function') {
      onClose();
    }
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleCloseFlyout = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpenFlyoutKey(null);
    }, 180);
  };

  const openFlyout = useCallback((key) => {
    clearCloseTimer();
    const el = railItemRefs.current[key];
    if (el) {
      const rect = el.getBoundingClientRect();
      setFlyoutTop(rect.top);
    }
    setOpenFlyoutKey(key);
  }, []);

  useEffect(() => () => clearCloseTimer(), []);

  const activeFlyoutItem = railItems.find((item) => item.key === openFlyoutKey);

  const renderRailItem = (item) => {
    if (item.type === 'dropdown') {
      const childActive = isDropdownActive(item);
      const isFlyoutOpen = openFlyoutKey === item.key;
      const active = childActive || isFlyoutOpen;

      return (
        <div
          key={item.key || item.id}
          ref={(el) => { railItemRefs.current[item.key] = el; }}
          className={`rail-item${active ? ' active' : ''}`}
          onMouseEnter={() => !isMobileView && openFlyout(item.key)}
          onMouseLeave={() => !isMobileView && scheduleCloseFlyout()}
          onClick={() => isMobileView && openFlyout(item.key)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openFlyout(item.key);
            }
          }}
        >
          <RailIcon name={item.icon} />
          <span>{getShortLabel(item.label)}</span>
          <div className="has-sub-mark" aria-hidden="true" />
        </div>
      );
    }

    const active = isActive(item.path);
    return (
      <div
        key={item.id || item.path}
        className={`rail-item${active ? ' active' : ''}`}
        onClick={() => handleNavigate(item.path)}
        role="link"
        tabIndex={0}
        title={item.label}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleNavigate(item.path);
          }
        }}
      >
        <RailIcon name={item.icon} />
        <span>{getShortLabel(item.label)}</span>
      </div>
    );
  };

  return (
    <>
      <div
        className="sidebar-rail show"
        id="leftmenusection"
        aria-hidden="false"
      >
        {railItems.map((item) => renderRailItem(item))}
      </div>

      {activeFlyoutItem && (
        <div
          className="sidebar-flyout open"
          style={{ top: flyoutTop }}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleCloseFlyout}
        >
          <div className="flyout-title">{activeFlyoutItem.label}</div>
          {activeFlyoutItem.children.map((child) => (
            <div
              key={child.path || child.action}
              className={`flyout-item${child.path && isActive(child.path) ? ' active' : ''}${child.disabled ? ' disabled' : ''}`}
              onClick={() => handleChildNavigate(child)}
              role="button"
              tabIndex={child.disabled ? -1 : 0}
              aria-disabled={child.disabled ? 'true' : undefined}
              title={
                child.disabled
                  ? child.path?.includes('lookup-ccm')
                    ? 'Practice is not enrolled in CCM'
                    : child.path?.includes('lookup-rpm')
                      ? 'Practice is not enrolled in RPM'
                      : 'Not available'
                  : undefined
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleChildNavigate(child);
                }
              }}
            >
              <RailIcon name={child.icon || activeFlyoutItem.icon} className="flyout-icon" />
              <span>{getShortLabel(child.label)}</span>
            </div>
          ))}
        </div>
      )}

      <button
        className="sidebar-help-fab"
        aria-label="Open chatbot assistant"
        title="Help & Assistant"
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent('evitals-ai-toggle'))}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" strokeWidth="2" />
          <path d="M9.1 9a3 3 0 115.5 1.7c-.6.9-1.6 1.3-1.6 2.3v.5" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" />
          <line x1="12" y1="17" x2="12" y2="17.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </>
  );
};

export default Sidebar;
